/**
 * Extern Labs MOCK INTERVIEW, the guide-page widget that replaces the Labs preview on a subset of
 * guide pages (Sep 2026). A reader who searched "Deloitte internship" gets a 90-second first-round
 * practice interview for that company, a scorecard, and a real externship that fills the gap.
 *
 * 🔴 A DISJOINT VOCABULARY FROM `labs_preview_*`, ON PURPOSE. The two widgets run side by side on
 * matched guide pages in the same slot, and the whole point is to compare them. If they shared event
 * names, a careless funnel would pool the two arms into one number. Every name here is
 * `labs_interview_*`, and every event carries `guide_source`, so the comparison is always "the same
 * step, on the pages that carried each widget".
 *
 * The comparable steps, in order: EMBED_OPENED (host click, same as `labs_embed_opened`) → STARTED →
 * COMPLETED → EMAIL_SUBMITTED → SIGNUP_HANDOFF. Accounts are read on the account record via
 * `utm_medium = interview-embed`, which does not depend on cookie consent.
 */
export const LABS_INTERVIEW_EVENTS = {
  /**
   * The reader answered the first question on the GUIDE PAGE and the interview opened. Fired by the
   * snippet on the article's own analytics, exactly like `labs_embed_opened`, so the numerator and the
   * guide-page visit share one population.
   */
  EMBED_OPENED: 'labs_interview_embed_opened',
  /** The interview mounted. Denominator for everything that happens inside the frame. */
  VIEWED: 'labs_interview_viewed',
  /** The reader answered the first question (on the guide page, or on the hook screen here). */
  STARTED: 'labs_interview_started',
  /** Every answer, with round, attempt and whether it was right. The abandon curve reads off this. */
  ANSWERED: 'labs_interview_answered',
  /** The story builder finished. Carries the evidence level, which is the gap the externship fills. */
  STORY_BUILT: 'labs_interview_story_built',
  /** The scorecard was shown. "Completed the interview." */
  COMPLETED: 'labs_interview_completed',
  /** An email unlocked the model answers. Over COMPLETED = the email hook's pull. */
  EMAIL_SUBMITTED: 'labs_interview_email_submitted',
  /** Handed to sign-up from the externship card. */
  SIGNUP_HANDOFF: 'labs_interview_signup_handoff',
} as const;

export type LabsInterviewEventName = (typeof LABS_INTERVIEW_EVENTS)[keyof typeof LABS_INTERVIEW_EVENTS];
